import MapSpec from '../MapSpec'
import CatalogService from '../../service/CatalogService'
import MapService from '../../service/MapService'
import RasterTileDataStream from '../stream/RasterTileDataStream'
import RasterTileLayer from '../layer/RasterTileLayer'
import MercatorProjection from '../../gis/projection/MercatorProjection'
import Location from '../../gis/Location'
import Point from '../../geom/Point'
import MapObjectShape from '../element/MapObjectShape'
import FromOMBShapeDataStream from '../stream/FromOMBShapeDataStream'
import ShapeLayer from '../layer/ShapeLayer'
import LangUtils from '../../utils/LangUtils'
import MapObjectAnnotation from '../element/MapObjectAnnotation'
import FromOMBAnnotationDataStream from '../stream/FromOMBAnnotationDataStream'
import AnnotationLayer from '../layer/AnnotationLayer'
import MapElementSearchableObject from '../search/MapElementSearchableObject'
import MapObject from '../../model/MapObject'
import Size from '@/model/Size'
import OSMProjectionProxy from '@/map/projection/OSMProjectionProxy'
import OndagoProjectionProxy from '@/map/projection/OndagoProjectionProxy'
import ArtisticProjection from '@/gis/projection/ArtisticProjection'
import MercatorPlusProjection from '@/gis/projection/MercatorPlusProjection'
import CoveredBoundsProjection from '@/gis/projection/CoveredBoundsProjection'
import DeadAreaProjection from '@/gis/projection/DeadAreaProjection'
import LatLongBounds from '@/gis/LatLongBounds'
import MapObjectLayer from '../element/MapObjectLayer'
import MapObjectStatusDataStream from '../stream/MapObjectStatusDataStream'
import MapLayer from '@/model/MapLayer'
import LabelAnnotationLayer from '../layer/LabelAnnotationLayer'
import ArrayUtils from '../../utils/ArrayUtils'
import MapObjectDataStream from '@/map/stream/MapObjectDataStream'
import OSMUtils from '@/utils/OSMUtils'
import UUIDUtils from '@/utils/UUIDUtils';
import { ShowHtmlTabMapAction } from './ShowHtmlTabMapAction'

export default class FromOMBMapSpec extends MapSpec {

  #MERCATOR_PROJECTION = "mercator";
  #MERCATOR_PLUS_PROJECTION = "mercator+";
  #ARTISTIC_PROJECTION = "artistic";

  constructor(user, publisherId, mapBundleId, mapId, language, i18n, includesEmergenciesTerrainReports) {
    super();

    this.user = user;
    this.publisherId = publisherId;
    this.mapBundleId = mapBundleId;
    this.mapBundleVersion = 0;
    this.mapId = mapId;
    this.language = language;
    this.i18n = i18n;
    this.includesEmergenciesTerrainReports = includesEmergenciesTerrainReports;

    this.mapService = new MapService();
    this.catalogService = new CatalogService();

    this.mapObjectStructure = [];
    this.mapLayers = [];
    this.sortMethod = null

    this.projection = null;
    this.mapSize = null;

    this.minOSMZoom = 0;
    this.maxOSMZoom = 22;
    this.baseOSMZoom = 12;

    this.trailDirectionsEnum = null;
    this.trailDifficultiesEnum = null;
    this.trailTypesEnum = null;

    this.customActions = [];

    this.searchPanelStructure = null;
  }

  load(forceAllVisible = false, forceAllInteractive = false, skipAccessCodes = false, accessCodes = []) {
    var promise = new Promise((resolve, reject) => {
      this.mapService.loadMapForPublisher(this.publisherId, this.mapId).then((map) => {
        if (map) {
          var bundleId = null;
          for (var key in map.bundleIds) {
            if (map.bundleIds[key] == true) {
              bundleId = key;
              break;
            }
          }

          this.catalogService.loadCatalogBundle(bundleId).then((bundle) => {
            this.mapBundleVersion = bundle ? bundle.version : 0;
            this.sortMethod = map.getSortMethod()
            this.mapService.loadMapJSONDefinition(this.mapBundleId, map.mapDefinitionFilename).then((mapDefinition) => {
              if (mapDefinition) {
                this._extractProjection(mapDefinition);
                this._extractProjectionProxy(mapDefinition);
                this._extractMapLayers(mapDefinition, map, forceAllVisible);
                this._computeInitialCenterLocation(mapDefinition);
  
                this._extractName(mapDefinition);
  
                const bundleBaseUrl = this.mapService.buildUrlToMapBundle(this.mapBundleId);
  
                var jsonProjection = mapDefinition.projection;
                if (jsonProjection) {
                  if (jsonProjection.tiles && jsonProjection.tiles.length > 0) {
                    var index = 0;
                    for (var tile of jsonProjection.tiles) {
                      var scheme = "xyz";
                      var tilesUrl = this.mapService.buildUrlToTilesRepository(this.mapBundleId, this.mapId, tile.folder, tile.imageFormat);
  
                      var tilesUrls = [tilesUrl];
  
                      if (mapDefinition.projection && mapDefinition.projection.tiles && mapDefinition.projection.tiles.length > 0) {
                        const tileSpec = mapDefinition.projection.tiles[0];
                        if (tileSpec.tileServerUrl) {
                          tilesUrls = [tileSpec.tileServerUrl];
                        }
                        if (tileSpec.tileServerProtocol) {
                          scheme = tileSpec.tileServerProtocol.toLowerCase();
                        }
                      }
  
                      var rasterTileDataStream = new RasterTileDataStream("tiles_" + index, tilesUrls, tile.tileSize, tile.minZoom, tile.maxZoom, scheme);
                      var rasterTileLayer = new RasterTileLayer("tiles_" + index, rasterTileDataStream);
  
                      this.dataStreams.push(rasterTileDataStream);
                      this.layers.push(rasterTileLayer);
  
                      index = index + 1;
                    }
                  }
                } else {
                  // TODO
                }
  
                this.mapObjectStatusDataStream = new MapObjectStatusDataStream(
                  this.mapId + "_status",
                  this.publisherId,
                  this.mapId,
                  this.language,
                  map.statusAvailableOnMap,
                );
  
                var mapObjectStatusLayer = this._findMapObjectStatusMapLayer();
  
                if (mapDefinition.objects) {
                  var layerIndex = 0;
                  for (var object of mapDefinition.objects) {
                    if (skipAccessCodes || !object.accessCodes || ArrayUtils.isContainingAtLeastOne(object.accessCodes, accessCodes)) {
                      if (object.uid) {
                        object.uid = object.uid.replace(",", "_");
                      }
  
                      if (object.type == "layer") {
                        this.mapObjectStructure.push(new MapObject(object));
  
                        const mapElement = new MapObjectLayer(object, this.language);
  
                        if (object.visibleInSelector == true || object.visibleInSelector == undefined) {
                          if (this._hasSearchableChildren(object)) {
                            if (!this._containsSearchableObject(object.uid, this.searchableObjects)) {
                              const searchableObject = new MapElementSearchableObject(mapElement, this.language, bundleBaseUrl);
                              this.searchableObjects.push(searchableObject);
                            }
                          }
                        }
  
                        if (object.children) {
                          const allShapes = [];
                          const allAnnotations = [];
  
                          const childrenMapElements = this._processMapObjects(forceAllInteractive, null, object.visibleInSelector == undefined || object.visibleInSelector == true, object.children,
                            allShapes, allAnnotations, mapDefinition, bundleBaseUrl, object.hidden);
                          mapElement.children = childrenMapElements;
  
                          if (allShapes.length > 0) {
                            const id = this._getMapObjectId(object) + "_" + layerIndex + "_shapes";
                            const dataStream = new FromOMBShapeDataStream(id, allShapes, this.mapObjectStatusDataStream, forceAllVisible);
                            this.dataStreams.push(dataStream);
  
                            const selectionDataStream = new FromOMBShapeDataStream(id + "_selection", [], null, forceAllVisible);
                            this.dataStreams.push(selectionDataStream);
  
                            const layer = new ShapeLayer(id, dataStream, selectionDataStream, ShapeLayer.NORMAL_STYLE_PROPERTIES);
                            this.layers.push(layer);
  
                            if (mapObjectStatusLayer) {
                              const statusVisibleLayer = new ShapeLayer(id + "_statusVisible", dataStream, selectionDataStream, ShapeLayer.STATUS_VISIBLE_STYLE_PROPERTIES, mapObjectStatusLayer);
                              this.layers.push(statusVisibleLayer);
                            }
  
                            const selectionLayer = new ShapeLayer(id + "_selection", selectionDataStream, null, ShapeLayer.SELECTED_STYLE_PROPERTIES);
                            this.layers.push(selectionLayer);
                          }
  
                          if (allAnnotations.length > 0) {
                            const id = this._getMapObjectId(object) + "_" + layerIndex + "_annotations";
                            const dataStream = new FromOMBAnnotationDataStream(id, allAnnotations, bundleBaseUrl, this.mapObjectStatusDataStream, forceAllVisible);
                            this.dataStreams.push(dataStream);
  
                            const layer = new AnnotationLayer(id, dataStream);
                            this.layers.push(layer);
  
                            if (mapObjectStatusLayer) {
                              const statusVisibleLayer = new LabelAnnotationLayer(id + "_statusVisible", dataStream, null,
                                LabelAnnotationLayer.STATUS_VISIBLE_STYLE_PROPERTIES, mapObjectStatusLayer);
                              this.layers.push(statusVisibleLayer);
                            }
                          }
                        }
                      }
                    }
  
                    layerIndex = layerIndex + 1;
                  }
  
                  if (this.includesEmergenciesTerrainReports) {
                    const emergenciesDataStream = new MapObjectDataStream("emergencies", this.mapService, false, this.user, this.publisherId, this.mapId, MapObject.PoiType.EMERGENCY);
                    this.dataStreams.push(emergenciesDataStream);
                    this.layers.push(new AnnotationLayer("emergencies", emergenciesDataStream, null, null));
  
                    const terrainReportPOIDataStream = new MapObjectDataStream("terrain_reports", this.mapService, false, this.user, this.publisherId, this.mapId, MapObject.PoiType.TERRAIN_REPORT);
                    this.dataStreams.push(terrainReportPOIDataStream);
                    this.layers.push(new AnnotationLayer("terrain_reports", terrainReportPOIDataStream, null, null));
  
                    const normalPOIDataStream = new MapObjectDataStream("normal", this.mapService, false, this.user, this.publisherId, this.mapId, MapObject.PoiType.NORMAL);
                    this.dataStreams.push(normalPOIDataStream);
                    this.layers.push(new AnnotationLayer("normal", normalPOIDataStream, null, null));
                  }
  
                  this._sortLayers();
                  this._loadAttributions(mapDefinition, bundleBaseUrl);
                  this._loadLanguageAvailability(map);
                } else {
                  // TODO
                }
  
                this._extractCustomActions(mapDefinition);
  
                this.searchPanelStructure = mapDefinition.searchPanelStructure;
              } else {
                // TODO
              }
  
              resolve();
            }).catch((error) => {
              reject(error);
            })
          }).catch((error) => {
            reject(error);
          });
        }
      }).catch((error) => {
        console.log(error);
      });
    });

    return promise;
  }

  toJSON() {
    const json = {
      streams: []
    };

    var topLatitude = -90.0;
    var leftLongitude = 180.0;
    var bottomLatitude = 90.0;
    var rightLongitude = -180.0;

    for (var dataStream of this.dataStreams) {
      if (dataStream instanceof FromOMBAnnotationDataStream) {
        const dataStreamJSON = dataStream.toJSON();

        if (dataStreamJSON.topLeftBound && dataStreamJSON.bottomRightBound) {
          topLatitude = Math.max(topLatitude, dataStreamJSON.topLeftBound.latitude);
          leftLongitude = Math.min(leftLongitude, dataStreamJSON.topLeftBound.longitude);
          bottomLatitude = Math.min(bottomLatitude, dataStreamJSON.bottomRightBound.latitude);
          rightLongitude = Math.max(rightLongitude, dataStreamJSON.bottomRightBound.longitude);
        }

        json.streams.push(dataStreamJSON);
      } else if (dataStream instanceof FromOMBShapeDataStream) {
        const dataStreamJSON = dataStream.toJSON();

        if (dataStreamJSON.topLeftBound && dataStreamJSON.bottomRightBound) {
          topLatitude = Math.max(topLatitude, dataStreamJSON.topLeftBound.latitude);
          leftLongitude = Math.min(leftLongitude, dataStreamJSON.topLeftBound.longitude);
          bottomLatitude = Math.min(bottomLatitude, dataStreamJSON.bottomRightBound.latitude);
          rightLongitude = Math.max(rightLongitude, dataStreamJSON.bottomRightBound.longitude);
        }
      }
    }

    json.topLeftBound = { latitude: topLatitude, longitude: leftLongitude };
    json.bottomRightBound = { latitude: bottomLatitude, longitude: rightLongitude };

    return json;
  }

  extractInteractiveMapElements(flatten) {
    const result = [];

    const bundleBaseUrl = this.mapService.buildUrlToMapBundle(this.mapBundleId)

    for (var mapObject of this.mapObjectStructure) {
      if (mapObject.type == "layer") {
        const mapElement = this._extractInteractiveMapElements(mapObject, bundleBaseUrl, this.mapBundleVersion);
        result.push(mapElement);
        if (mapElement.children && flatten) {
          for (const child of mapElement.children) {
            result.push(child);
          }
        }
      }
    }

    return result;
  }

  _extractInteractiveMapElements(mapObject, baseUrl, mapBundleVersion) {
    const mapElement = {
      id: mapObject.uid,
      type: mapObject.type
    };

    if (!mapElement.id) {
      mapElement.id = UUIDUtils.generateUUID();
    }

    if (typeof mapObject.name === 'string') {
      mapElement["name"] = {
        "en": mapObject.name
      };
    } else {
      mapElement.name = mapObject.name;
    }

    if (mapObject.iconFileName) {
      var iconFileName = LangUtils.valueForLanguage(mapObject.iconFileName, this.language);
      iconFileName = iconFileName.replace(".png", "@2x.png");
      iconFileName = iconFileName.replace(".jpg", "@2x.jpg");

      mapElement.iconFileName = baseUrl + "/" + iconFileName + "?v=" + mapBundleVersion;
    }

    if (mapObject.children) {
      mapElement.children = [];
      for (const child of mapObject.children) {
        mapElement.children.push(this._extractInteractiveMapElements(child, baseUrl, mapBundleVersion));
      }
    }

    mapElement.trailInformation = mapObject.trailInformation;

    return mapElement;
  }

  getMapObjectStructure() {
    return this.mapObjectStructure;
  }

  getSearchPanelStructure() {
    return this.searchPanelStructure;
  }

  _extractProjection(mapDefinition) {
    var jsonProjection = mapDefinition.projection;
    if (jsonProjection) {
      if (jsonProjection.mapDimension) {
        this.mapSize = new Size(jsonProjection.mapDimension)
      }

      this.projection = this._createProjection(jsonProjection);
    } else {
      // TODO Handle cases where projection is not defined
    }
  }

  _hasSearchableChildren(mapObject) {
    if (mapObject.children && mapObject.children.length > 0) {
      for (const child of mapObject.children) {
        if (child.visibleInSelector || child.visibleInSelector == undefined) {
          return true;
        } else if (child.children && child.children.length > 0) {
          if (this._hasSearchableChildren(child)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  _extractProjectionProxy(mapDefinition) {
    this.projectionProxy = null;

    const jsonProjection = mapDefinition.projection;
    if (jsonProjection) {
      const tiles = jsonProjection.tiles;
      if (tiles && tiles.length == 1) {
        const tileDetail = tiles[0];
        if (tileDetail.tileType === "ONDAGO") {
          this.projectionProxy = new OndagoProjectionProxy(this.projection, tileDetail.tileSize, tileDetail.baseZoom, this.mapSize, 256);
        } else {
          this.projectionProxy = new OSMProjectionProxy(this.projection);
        }

        if (tileDetail.baseZoom) {
          this.baseOSMZoom = tileDetail.baseZoom;
        }
        if (tileDetail.minZoom) {
          this.minOSMZoom = tileDetail.minZoom;
        }
        if (tileDetail.maxZoom) {
          this.maxOSMZoom = tileDetail.maxZoom;
        }

        const mapMaxZoom = mapDefinition.maxZoom;
        if (mapMaxZoom) {
          const osmMapMaxZoom = OSMUtils.zoomFromZoomScale(mapMaxZoom, this.baseOSMZoom);
          this.maxOSMZoom = Math.max(this.maxOSMZoom, osmMapMaxZoom);
        }

        // const mapInitialZoom = mapDefinition.initialZoom;
        // if (mapInitialZoom) {
        //     this.initialOSMZoom = OSMUtils.zoomFromZoomScale(mapInitialZoom, this.baseOSMZoom);
        //     if (this.initialOSMZoom < this.minOSMZoom) {
        //         this.initialOSMZoom = this.minOSMZoom;
        //     }
        // } else {
        //     this.initialOSMZoom = this.baseOSMZoom;
        // }
        this.initialOSMZoom = this.minOSMZoom;
      }

      this.topLeftCorner = this.projectionProxy.toWorldLocation(new Point(0, 0));
      this.bottomRightCorner = this.projectionProxy.toWorldLocation(new Point(jsonProjection.mapDimension.width, jsonProjection.mapDimension.height));
    }

    if (!this.projectionProxy) {
      this.projectionProxy = new OSMProjectionProxy(this.projection);
    }
  }

  _extractMapLayers(mapDefinition, map, forceAllVisible) {
    if (forceAllVisible || map.statusAvailableOnMap) {
      const mapObjectStatusLayer = new MapLayer();
      mapObjectStatusLayer.title = {
        "default": this.i18n.t("map_detail.layers_panel.map_object_status")
      }
      mapObjectStatusLayer.forMapObjectStatus = true;
      mapObjectStatusLayer.hideable = true;
      mapObjectStatusLayer.hidden = !map.statusVisibleByDefault;
      this.mapLayers.push(mapObjectStatusLayer);
    }

    if (mapDefinition.mapLayers) {
      // TODO Support all types of layers.
    }
  }

  _extractCustomActions(mapDefinition) {
    if (mapDefinition.tabs) {
      for (const tab of mapDefinition.tabs) {
        if (tab.type == "html") {
          if (tab.parameters.filename) {
            const mapAction = new ShowHtmlTabMapAction(tab.uid, tab.name, tab.iconFileName, tab.parameters.filename);
            this.customActions.push(mapAction);
          }
        }
      }
    }
  }

  _processMapObjects(forceAllInteractive, masterMapElement, canAddSearchableObjects, mapObjects, allShapes, allAnnotations, mapDefinition, bundleBaseUrl, parentIsHidden) {
    const createdMapElements = [];

    for (var mapObject of mapObjects) {
      const normalStyle = this._findStyleById(mapObject.style, this.language, mapDefinition.styles);
      const selectedStyle = this._findStyleById(mapObject.selectedStyle, this.language, mapDefinition.styles);

      var mapElement = null;

      // Adding code to normalize the map object type. In some situations (Veloroute des Bleuets map objects), the
      // type attribute is initialized to poi which is the same thing as point. So it is forced here to point.
      if (mapObject.type == "poi") {
        mapObject.type = "point";
      }

      if (mapObject.type == "path" || mapObject.type == "svgPath") {
        mapElement = new MapObjectShape(mapObject, normalStyle, selectedStyle, this.projection, this.language);
        allShapes.push(mapElement);
      } else if (mapObject.type == "point") {
        mapElement = new MapObjectAnnotation(mapObject, normalStyle, selectedStyle, this.projection, this.language, this.mapBundleVersion);
        allAnnotations.push(mapElement);
      }

      if (parentIsHidden) {
        mapElement.setHidden(true);
      }

      mapElement.setInteractive(forceAllInteractive || mapObject.interactive == undefined || mapObject.interactive == true);

      if (canAddSearchableObjects && (mapObject.visibleInSelector == true || mapObject.visibleInSelector == undefined)) {
        if (!this._containsSearchableObject(mapObject.uid, this.searchableObjects)) {
          const searchableObject = new MapElementSearchableObject(mapElement, this.language, bundleBaseUrl);
          this.searchableObjects.push(searchableObject);
        }
      }

      var masterMapElementToUse = masterMapElement;
      if (masterMapElement) {
        masterMapElement.getChildren().push(mapElement);
        mapElement.masterMapElement = masterMapElement;
      } else {
        masterMapElementToUse = mapElement;
      }

      const trailInfo = mapObject.trailInformation;
      if (trailInfo) {
        const mapEnumerations = mapDefinition.enumerations;
        if (mapEnumerations) {
          if (trailInfo.difficulty) {
            const difficultyEnumeration = mapEnumerations.trailDifficulties;
            if (difficultyEnumeration) {
              trailInfo.difficulty = this._findEnumerationValue(trailInfo.difficulty, difficultyEnumeration);
            }
          }

          if (trailInfo.direction) {
            const directionEnumeration = mapEnumerations.trailDirections;
            if (directionEnumeration) {
              trailInfo.direction = this._findEnumerationValue(trailInfo.direction, directionEnumeration);
            }
          }

          if (trailInfo.type) {
            const typeEnumeration = mapEnumerations.trailTypes;
            if (typeEnumeration) {
              trailInfo.type = this._findEnumerationValue(trailInfo.type, typeEnumeration);
            }
          }
        }
      }

      if (mapElement) {
        if (mapObject.children && mapObject.children.length > 0) {
          this._processMapObjects(forceAllInteractive, masterMapElementToUse, false, mapObject.children, allShapes, allAnnotations, mapDefinition, bundleBaseUrl, parentIsHidden);
        }

        createdMapElements.push(mapElement);
      }
    }

    return createdMapElements;
  }

  _containsSearchableObject(searchableObjectId, searchableObjects) {
    for (var searchableObject of searchableObjects) {
      if (searchableObject.getId() == searchableObjectId) {
        return true;
      }
    }

    return false;
  }

  _findEnumerationValue(enumerationValueToFind, enumerationContent) {
    for (const enumeration of enumerationContent) {
      if (enumeration.value == enumerationValueToFind) {
        return enumeration;
      }
    }

    return null;
  }

  _computeInitialCenterLocation(mapDefinition) {
    if (mapDefinition.defaultCenter) {
      if (mapDefinition.defaultCenter.latitude) {
        this.initialCenterLocation = new Location(mapDefinition.defaultCenter.latitude, mapDefinition.defaultCenter.longitude);
      } else {
        const centerPoint = new Point(mapDefinition.defaultCenter.x, mapDefinition.defaultCenter.y);
        this.initialCenterLocation = this.projection.pointToLocation(centerPoint);
      }
    } else if (mapDefinition.initialContentOffset) {
      const centerPoint = new Point(mapDefinition.initialContentOffset.x, mapDefinition.initialContentOffset.y);
      this.initialCenterLocation = this.projection.pointToLocation(centerPoint);
    } else if (mapDefinition.projection.mapDimension) {
      const centerPoint = new Point(mapDefinition.projection.mapDimension.width / 2, mapDefinition.projection.mapDimension.height / 2);
      this.initialCenterLocation = this.projection.pointToLocation(centerPoint);
    }
  }

  _extractName(mapDefinition) {
    if (mapDefinition.name) {
      var name = LangUtils.valueForLanguage(mapDefinition.name, this.language);
      if (name) {
        if (name instanceof String) {
          this.name = name;
        } else {
          // TODO Patch for FQCK maps name, who is wrongly nested in an extra map level
          if (name["fr"]) {
            this.name = name["fr"];
          } else {
            this.name = name;
          }
        }
      }
    }
  }

  _getMapObjectId(mapObject) {
    if (mapObject) {
      if (mapObject.uid) {
        return mapObject.uid;
      } else if (mapObject.id) {
        return mapObject.id;
      } else if (mapObject.name) {
        return LangUtils.valueForLanguage(mapObject.name, "en");
      }
    }
    return null;
  }

  _findStyleById(styleIds, language, styles) {
    const styleId = styleIds instanceof String ? styleIds : LangUtils.valueForLanguage(styleIds, language);
    for (const style of styles) {
      if (style.id == styleId) {
        return style;
      }
    }
    return null;
  }

  _sortLayers() {
    // TODO PATCH: With generated FQCK maps, shape layers are above annotation layers. To prevent re-generating all the maps, a sort is applied to them as a patch.
    if (this.mapBundleId.startsWith("FQCK")) {
      const newLayers = [];

      for (const layer of this.layers) {
        if (!(layer instanceof AnnotationLayer)) {
          newLayers.push(layer);
        }
      }

      for (const layer of this.layers) {
        if (layer instanceof AnnotationLayer) {
          newLayers.push(layer);
        }
      }

      this.layers = newLayers;
    }
  }

  _computeOSMZoom(opticalZoom, baseOSMZoom) {
    return opticalZoom * baseOSMZoom;
  }

  _loadAttributions(mapDefinition, bundleBaseUrl) {
    if (mapDefinition) {
      if (mapDefinition.creditsDocumentFileName) {
        this.attributionsHTMLUrl = bundleBaseUrl + "/" + LangUtils.valueForLanguage(mapDefinition.creditsDocumentFileName, this.language);
      }
    }
  }

  _loadLanguageAvailability(map) {
    if (map.languageAvailability) {
      this.languageAvailability = map.languageAvailability;
    }
  }

  _createProjection(jsonProjection) {
    var result = null;

    const references = jsonProjection.referencePoints;
    const referenceAreas = jsonProjection.referenceAreas;
    const deadAreas = jsonProjection.deadAreas;
    const coveredBounds = jsonProjection.coveringBounds;

    const type = jsonProjection.type;
    if ((references && references.length > 0) || (referenceAreas && referenceAreas.length > 0)) {
      var referenceObjects = [];
      if (references) {
        for (var i = 0; i < references.length; i++) {
          var ref = references[i];
          if (ref) {
            referenceObjects.push(this._createReferencePoint(ref));
          }
        }
      }
      var areasObjects = [];
      if (referenceAreas) {
        for (var j = 0; j < referenceAreas.length; j++) {
          var area = referenceAreas[j];
          if (area) {
            var newArea = this._createReferenceArea(area);
            areasObjects.push(newArea);
          }
        }
      }
      var deadAreasObjects = [];
      if (deadAreas) {
        for (j = 0; j < deadAreas.length; j++) {
          var deadArea = deadAreas[j];
          if (deadArea) {
            var newDeadArea = this._createDeadArea(deadArea);
            deadAreasObjects.push(newDeadArea);
          }
        }
      }
      var mapRotation = (jsonProjection.mapRotation) ? jsonProjection.mapRotation : 0;
      var anchor = (mapRotation != 0 && jsonProjection.mapAnchor) ? new Point(jsonProjection.mapAnchor.x, jsonProjection.mapAnchor.y) : null;
      var mapCenter = new Point(this.mapSize.width / 2, this.mapSize.height / 2);
      var offset = (mapRotation != 0 && anchor) ? new Point(anchor.x - mapCenter.x, anchor.y - mapCenter.y) : null;
      if (type == this.#MERCATOR_PROJECTION && referenceObjects.length == 2 && areasObjects.length == 0) {
        result = new MercatorProjection(referenceObjects[0].point, referenceObjects[0].location, referenceObjects[1].point, referenceObjects[1].location);
      } else if (type == this.#ARTISTIC_PROJECTION && (referenceObjects.length > 0 || areasObjects.length > 0)) {
        result = new ArtisticProjection(areasObjects, referenceObjects);
      } else if (type == this.#MERCATOR_PLUS_PROJECTION) {
        result = new MercatorPlusProjection(areasObjects, referenceObjects, mapRotation, anchor, offset);
      }

      if (coveredBounds) {
        var cBounds = new LatLongBounds(new Location(coveredBounds.minLat, coveredBounds.minLong), new Location(coveredBounds.maxLat, coveredBounds.maxLong));
        result = new CoveredBoundsProjection(cBounds, result);
      }

      if (deadAreasObjects && deadAreasObjects.length > 0) {
        result = new DeadAreaProjection(deadAreasObjects, result);
      }
    }

    return result;
  }

  _createDeadArea(jsonArea) {
    if (jsonArea.shapePoints) {
      var newDeadArea = { shapePoints: [] };
      for (var i = 0; i < jsonArea.shapePoints.length; i++) {
        var point = new Point(jsonArea.shapePoints[i].x, jsonArea.shapePoints[i].y);
        newDeadArea.shapePoints.push(point);
      }
      return newDeadArea;
    }
    return null;
  }

  _createReferenceArea(jsonArea) {
    if (jsonArea.referenceShape && jsonArea.referenceShape.length >= 2) {
      var newArea = { shapePoints: [], shapeLocations: [] };
      for (var i = 0; i < jsonArea.referenceShape.length; i++) {
        var point = new Point(jsonArea.referenceShape[i].x, jsonArea.referenceShape[i].y);
        var location = new Location(jsonArea.referenceShape[i].latitude, jsonArea.referenceShape[i].longitude);
        newArea.shapePoints.push(point);
        newArea.shapeLocations.push(location);
      }
      if (jsonArea.referencePoints) {
        newArea.referencePoints = [];
        for (i = 0; i < jsonArea.referencePoints.length; i++) {
          newArea.referencePoints.push(this._createReferencePoint(jsonArea.referencePoints[i]));
        }
      }
      if (jsonArea.referenceAreas) {
        newArea.referenceAreas = [];
        for (i = 0; i < jsonArea.referenceAreas.length; i++) {
          newArea.referenceAreas.push(this._createReferenceArea(jsonArea.referenceAreas[i]));
        }
      }
      return newArea;
    }
    return null;
  }

  _createReferencePoint(object) {
    return {
      point: new Point(object.coordinate.x, object.coordinate.y),
      location: new Location(object.location.latitude, object.location.longitude)
    };
  }

  _findMapObjectStatusMapLayer() {
    for (var mapLayer of this.mapLayers) {
      if (mapLayer.forMapObjectStatus) {
        return mapLayer;
      }
    }
    return null;
  }
}
